/**
 * Shared DTOs and Zod schemas for the push-notifications route.
 *
 * Each row represents one device's opt-in to web-push reminders.
 * The `endpoint` URL is the natural key — a single user typically
 * has several (phone PWA, desktop PWA, tablet); re-subscribing the
 * same device updates the existing row rather than inserting a
 * duplicate.
 */

import { z } from 'zod';

/**
 * Shape the browser hands back from `pushManager.subscribe()` after
 * JSON-serialising. The client unpacks it into this flat shape
 * before POSTing — keeps the API surface predictable and the
 * server free to validate every field.
 *
 * `timezone` lands here too so the cron worker can translate
 * obligation `HH:MM` values into UTC instants. It's stored on the
 * user record (not per-subscription) since the user's home TZ is
 * a steady-state property; the field on subscribe is just the most
 * recent value the browser saw.
 */
export const pushSubscriptionCreateSchema = z.object({
  endpoint: z.string().url().min(20).max(2048),
  keys: z.object({
    p256dh: z.string().min(20).max(200),
    auth: z.string().min(8).max(50),
  }),
  userAgent: z.string().max(500).optional(),
  timezone: z.string().min(1).max(64).optional(),
  // Stable per-device UUID kept in localStorage. Lets the server
  // recognise "same device, fresh endpoint" after a SW
  // replacement and update the existing row instead of inserting
  // a duplicate. Optional for backwards compatibility with
  // pre-deviceId clients.
  deviceId: z.string().uuid().optional(),
});

export const pushSubscriptionDeleteSchema = z.object({
  endpoint: z.string().url().min(20).max(2048),
});

export type PushSubscriptionCreateInput = z.infer<typeof pushSubscriptionCreateSchema>;
export type PushSubscriptionDeleteInput = z.infer<typeof pushSubscriptionDeleteSchema>;

export interface PushSubscriptionDTO {
  id: string;
  endpoint: string;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  /** Timestamp of the last SW push-ack POST from this device.
   *  Null until the first ack arrives. */
  lastAckedAt: string | null;
  /** Pushes the cron has fired to this subscription that the
   *  device hasn't acknowledged. Reset to 0 on every ack. The
   *  client uses this to detect a silently-broken subscription
   *  and prompt the user to reinstall via Safari. */
  pushesSinceAck: number;
}

/** Tiny payload posted by the SW after handling a push. Endpoint
 *  is the natural credential — knowing it means the SW running
 *  for that subscription called us. */
export const pushAckSchema = z.object({
  endpoint: z.string().url().min(20).max(2048),
});

export type PushAckInput = z.infer<typeof pushAckSchema>;
